import React from 'react';
import { Youtube, Play, Search } from 'lucide-react';

export default function VideoAlarm({
  alarmHour,
  alarmMinute,
  snoozeMinutes,
  selectedMelody,
  growing,
  alarmActive,
  alarmRinging,
  timeLeft,
  setAlarmHour,
  setAlarmMinute,
  setSnoozeMinutes,
  setSelectedMelody,
  setGrowing,
  setAlarmActive,
  stopAlarm,
  snoozeAlarm,
  testSound,
  // 视频相关 props
  youtubeUrl,
  setYoutubeUrl,
  videoId,
  setVideoId,
  volume,
  setVolume,
  ytApiReady,
  showPlayPrompt,
  audioUnlocked,
  popularVideos,
  unlockAudio,
  playAlertSound,
  createYouTubePlayer,
  manualStartPlay,
  stopVideo,
  extractVideoId
}) {
  const handleUrlChange = (url) => {
    setYoutubeUrl(url);
    const id = extractVideoId(url);
    if (id) {
      setVideoId(id);
      if (!audioUnlocked) {
        unlockAudio();
      }
    }
  };

  const selectPopularVideo = (id) => {
    setVideoId(id);
    setYoutubeUrl(`https://www.youtube.com/watch?v=${id}`);
    if (!audioUnlocked) {
      unlockAudio();
    }
  };

  const handleSetAlarm = () => {
    setAlarmActive(!alarmActive);
    if (!audioUnlocked) {
      unlockAudio();
    }
  };

  const handleStopAlarm = () => {
    stopVideo();
    stopAlarm();
  };

  return (
    <div className="space-y-6">
      <div className="bg-cyan-600/40 backdrop-blur-md rounded-3xl p-8 border border-cyan-400/30 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <Youtube className="w-8 h-8 text-white" />
          <h3 className="text-2xl font-bold text-white">Video Alarm Clock</h3>
        </div>

        <p className="text-white/80 mb-6">
          Wake up to your favorite YouTube video. Enter a video URL or choose from popular options.
        </p>

        {/* Time Settings */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-white font-semibold mb-3 text-lg">
              When alarm?
            </label>
            <div className="flex gap-3">
              <select
                value={alarmHour}
                onChange={(e) => setAlarmHour(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/90 rounded-xl text-gray-700 font-semibold text-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i.toString().padStart(2, '0')}>
                    {i.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
              <select
                value={alarmMinute}
                onChange={(e) => setAlarmMinute(e.target.value)}
                className="flex-1 px-4 py-3 bg-white/90 rounded-xl text-gray-700 font-semibold text-xl focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i.toString().padStart(2, '0')}>
                    {i.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-white font-semibold mb-3 text-lg">
              Volume: {volume}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="w-full h-3 bg-white/30 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* YouTube URL Input */}
        <div className="mb-6">
          <label className="block text-white font-semibold mb-3 text-lg">
            YouTube Video URL
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={youtubeUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="flex-1 px-4 py-3 bg-white/90 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
            <button
              disabled={!videoId}
              className="p-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-500 disabled:cursor-not-allowed rounded-xl transition-colors"
            >
              <Search className="w-6 h-6 text-white" />
            </button>
          </div>
          {videoId && (
            <p className="text-green-300 text-sm mt-2">✓ Valid video ID: {videoId}</p>
          )}
        </div>

        {/* Popular Videos */}
        <div className="mb-6">
          <label className="block text-white font-semibold mb-3 text-lg">
            Or Choose Popular Wake-up Videos
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {popularVideos.map(video => (
              <button
                key={video.id}
                onClick={() => selectPopularVideo(video.id)}
                className={`p-3 rounded-xl text-left transition-colors ${
                  videoId === video.id
                    ? 'bg-cyan-500 text-white'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  <span className="text-sm">{video.title}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* YouTube Player Container */}
        {!alarmRinging ? (
          <div className="space-y-4">
            <button
              onClick={handleSetAlarm}
              disabled={!videoId}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-colors shadow-lg disabled:bg-gray-500 disabled:cursor-not-allowed ${
                alarmActive
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-cyan-500 hover:bg-cyan-600 text-white'
              }`}
            >
              {alarmActive ? '✓ Video Alarm Set' : 'Set Video Alarm'}
            </button>
          </div>
        ) : (
          <div className="flex gap-4">
            <button
              onClick={handleStopAlarm}
              className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg"
            >
              Stop Video Alarm
            </button>
            <button
              onClick={snoozeAlarm}
              className="flex-1 py-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-bold text-lg transition-colors shadow-lg"
            >
              Snooze ({snoozeMinutes} min)
            </button>
          </div>
        )}

        {/* Status Messages */}
        {alarmActive && !alarmRinging && (
          <div className="mt-4 bg-green-100 border-2 border-green-500 rounded-lg p-4">
            <p className="text-green-800 font-semibold">
              Video alarm set for {alarmHour}:{alarmMinute}
            </p>
            <p className="text-green-600 text-sm mt-1">
              Time left: {timeLeft}
            </p>
          </div>
        )}
      </div>


    </div>
  );
}